import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Check, Download, Heart, Loader2, Lock, Phone, Play, ShoppingBag, Sparkles, Trash2 } from "lucide-react";
import { DemoCard, useGuide } from "./Guide";

/* ---------------- 01 · Buttons ---------------- */

export function PlainButton() {
    const { say } = useGuide();
    return (
        <DemoCard title="Just a button" hint="click" testid="demo-plain-button">
            <button onClick={() => say("That's just a button. Boring, right? Keep going — every one of these is something we can put on your site.", "plain")}
                className="rounded-xl bg-zinc-700 px-5 py-2.5 text-sm font-bold text-[#ffffff] transition-colors hover:bg-zinc-600" data-testid="demo-plain-btn">
                Click me
            </button>
        </DemoCard>
    );
}

export function GlowButton() {
    const { say } = useGuide();
    return (
        <DemoCard title="The one that glows" hint="hover + click" testid="demo-glow-button">
            <button onMouseEnter={() => say("Hover states matter. This is the button people actually click — it looks alive before they touch it.", "glow-hover")}
                onClick={() => say("We can make your buttons exactly like this one. Your colors, your words, your energy.", "glow")}
                className="relative rounded-full bg-brand-green px-6 py-2.5 text-sm font-black uppercase tracking-wide text-black shadow-[0_0_0_0_rgba(37, 99, 235,0.6)] transition-all duration-300 hover:shadow-[0_0_30px_6px_rgba(37, 99, 235,0.35)]"
                data-testid="demo-glow-btn">
                Order now
            </button>
        </DemoCard>
    );
}

export function RippleButton() {
    const { say } = useGuide();
    const [ripples, setRipples] = useState([]);
    const fire = (e) => {
        const r = e.currentTarget.getBoundingClientRect();
        setRipples((rs) => [...rs, { id: Date.now(), x: e.clientX - r.left, y: e.clientY - r.top }]);
        say("Ripple feedback. Tiny detail, but it's the difference between 'a website' and 'wow, who built this?'", "ripple");
    };
    return (
        <DemoCard title="Ripple on tap" hint="tap anywhere" testid="demo-ripple-button">
            <button onClick={fire} className="relative overflow-hidden rounded-xl border border-white/15 bg-white/[0.06] px-6 py-3 text-sm font-bold" data-testid="demo-ripple-btn">
                Touch it
                {ripples.map((r) => (
                    <motion.span key={r.id} initial={{ scale: 0, opacity: 0.55 }} animate={{ scale: 6, opacity: 0 }} transition={{ duration: 0.75 }}
                        onAnimationComplete={() => setRipples((rs) => rs.filter((x) => x.id !== r.id))}
                        className="pointer-events-none absolute h-10 w-10 rounded-full bg-brand-green" style={{ left: r.x - 20, top: r.y - 20 }} />
                ))}
            </button>
        </DemoCard>
    );
}

export function LoadingButton() {
    const { say } = useGuide();
    const [state, setState] = useState("idle");
    const go = () => {
        setState("loading");
        say("Loading, then a confirmation. Your customer never wonders 'did that work?'", "loading");
        setTimeout(() => setState("done"), 1300);
        setTimeout(() => setState("idle"), 3600);
    };
    return (
        <DemoCard title="Loading → done" hint="click" testid="demo-loading-button">
            <button onClick={go} disabled={state !== "idle"}
                className={`inline-flex min-w-[11rem] items-center justify-center gap-2 rounded-xl px-5 py-3 text-sm font-bold transition-colors ${state === "done" ? "bg-brand-green text-black" : "bg-white/10 text-white hover:bg-white/15"}`}
                data-testid="demo-loading-btn">
                {state === "loading" && <Loader2 size={16} className="animate-spin" />}
                {state === "done" && <Check size={16} />}
                {state === "idle" ? "Place order" : state === "loading" ? "Sending…" : "Order placed"}
            </button>
        </DemoCard>
    );
}

export function MagneticButton() {
    const { say } = useGuide();
    const [d, setD] = useState({ x: 0, y: 0 });
    const move = (e) => {
        const r = e.currentTarget.getBoundingClientRect();
        setD({ x: (e.clientX - (r.left + r.width / 2)) / 4, y: (e.clientY - (r.top + r.height / 2)) / 4 });
    };
    return (
        <DemoCard title="Magnetic button" hint="move your mouse" testid="demo-magnetic-button">
            <div onMouseMove={move} onMouseLeave={() => setD({ x: 0, y: 0 })} onMouseEnter={() => say("It leans toward the cursor. People chase it. Chasing = clicking.", "magnetic")}
                className="flex h-24 items-center justify-center rounded-2xl border border-dashed border-white/12">
                <motion.button animate={d} transition={{ type: "spring", stiffness: 200, damping: 12 }}
                    className="rounded-full border border-brand-green/50 px-6 py-2.5 text-sm font-bold text-brand-green" data-testid="demo-magnetic-btn">
                    Catch me
                </motion.button>
            </div>
        </DemoCard>
    );
}

export function IconButtons() {
    const { say } = useGuide();
    const set = [
        { icon: Phone, label: "Call", line: "Tap-to-call. On a phone this dials you instantly — most Brooklyn shops are missing it." },
        { icon: ShoppingBag, label: "Cart", line: "Cart button with a live badge. Yes, we wire real carts." },
        { icon: Download, label: "PDF", line: "One-tap downloads — menus, price sheets, spec docs." },
        { icon: Heart, label: "Save", line: "Favorites. People come back for the list they built." },
    ];
    return (
        <DemoCard title="Icon buttons" hint="tap any" testid="demo-icon-buttons">
            <div className="flex flex-wrap gap-2.5">
                {set.map((s) => (
                    <button key={s.label} onClick={() => say(s.line, s.label)}
                        className="inline-flex items-center gap-2 rounded-xl border border-white/12 bg-white/[0.04] px-3.5 py-2.5 text-xs font-bold uppercase tracking-wide transition-colors hover:border-brand-green/50 hover:text-brand-green"
                        data-testid={`demo-icon-btn-${s.label.toLowerCase()}`}>
                        <s.icon size={14} /> {s.label}
                    </button>
                ))}
            </div>
        </DemoCard>
    );
}

export function GradientButton() {
    const { say } = useGuide();
    return (
        <DemoCard title="Premium gradient" hint="hover" testid="demo-gradient-button">
            <button onClick={() => say("Looks like a $50,000 site. Costs you a few thousand and one week.", "gradient")}
                className="group relative overflow-hidden rounded-2xl px-7 py-3.5 text-sm font-black uppercase tracking-widest text-black" data-testid="demo-gradient-btn">
                <span className="absolute inset-0 bg-[linear-gradient(110deg,#2563EB,45%,#7dd3fc,55%,#2563EB)] bg-[length:200%_100%] transition-all duration-700 group-hover:bg-[position:100%_0]" />
                <span className="relative">Go premium</span>
            </button>
        </DemoCard>
    );
}

export function CopyButton() {
    const { say } = useGuide();
    const [copied, setCopied] = useState(false);
    return (
        <DemoCard title="Copy to clipboard" hint="click" testid="demo-copy-button">
            <button onClick={() => { setCopied(true); say("Copy buttons for your phone, your Zelle, your address. Fewer typos, more orders.", "copy"); setTimeout(() => setCopied(false), 1800); }}
                className="flex w-full items-center justify-between rounded-xl border border-white/12 bg-brand-ink px-4 py-3 text-left text-sm hover:border-brand-green/50" data-testid="demo-copy-btn">
                <span className="font-mono">(347) 573-3875</span>
                <span className={`font-mono text-[11px] uppercase tracking-widest ${copied ? "text-brand-green" : "text-zinc-500"}`}>{copied ? "copied!" : "copy"}</span>
            </button>
        </DemoCard>
    );
}

export function ConfettiButton() {
    const { say } = useGuide();
    const [bits, setBits] = useState([]);
    const pop = () => {
        setBits(Array.from({ length: 26 }, (_, i) => ({ id: Date.now() + i, x: (Math.random() - 0.5) * 260, y: -Math.random() * 180 - 40, r: Math.random() * 360, c: ["#2563EB", "#7dd3fc", "#ffffff", "#fbbf24"][i % 4] })));
        say("Confetti when someone checks out. Silly? Maybe. Memorable? Definitely — and memorable sells.", "confetti");
        setTimeout(() => setBits([]), 1400);
    };
    return (
        <DemoCard title="Celebrate the sale" hint="pop it" testid="demo-confetti">
            <div className="relative flex h-24 items-center justify-center">
                <button onClick={pop} className="rounded-full bg-white px-6 py-2.5 text-sm font-black uppercase tracking-widest text-black" data-testid="demo-confetti-btn">Checkout</button>
                {bits.map((b) => (
                    <motion.span key={b.id} initial={{ x: 0, y: 0, opacity: 1, rotate: 0 }} animate={{ x: b.x, y: b.y, opacity: 0, rotate: b.r }} transition={{ duration: 1.2, ease: "easeOut" }}
                        className="pointer-events-none absolute h-2 w-2 rounded-sm" style={{ background: b.c }} />
                ))}
            </div>
        </DemoCard>
    );
}

export function LockedButton() {
    const { say } = useGuide();
    const [unlocked, setUnlocked] = useState(false);
    return (
        <DemoCard title="Members-only button" hint="unlock" testid="demo-locked-button">
            <button onClick={() => { setUnlocked(!unlocked); say(unlocked ? "Locked again. Gate wholesale pricing, member drops, whatever you want behind a login." : "Unlocked. Member-only pricing, hidden pages, VIP drops — we build the gate and the login.", "locked"); }}
                className={`inline-flex items-center gap-2 rounded-xl px-5 py-3 text-sm font-bold transition-colors ${unlocked ? "bg-brand-green text-black" : "border border-white/15 bg-white/[0.04] text-zinc-300"}`}
                data-testid="demo-locked-btn">
                {unlocked ? <Sparkles size={15} /> : <Lock size={15} />} {unlocked ? "Wholesale price: $3/ft" : "Members only"}
            </button>
        </DemoCard>
    );
}

export function DestructiveButton() {
    const { say } = useGuide();
    const [step, setStep] = useState(0);
    return (
        <DemoCard title="Are you sure?" hint="click twice" testid="demo-destructive">
            <button onClick={() => { const n = step === 0 ? 1 : 0; setStep(n); say(n ? "Two-step confirm. Nobody deletes their cart by accident." : "Cancelled. Undo, confirm, safety nets — real apps have them.", "destructive"); }}
                className={`inline-flex items-center gap-2 rounded-xl px-5 py-3 text-sm font-bold transition-colors ${step ? "bg-red-500 text-white" : "border border-red-500/40 text-red-400"}`}
                data-testid="demo-destructive-btn">
                <Trash2 size={15} /> {step ? "Tap again to confirm" : "Delete order"}
            </button>
        </DemoCard>
    );
}

export function VideoButton() {
    const { say } = useGuide();
    const [playing, setPlaying] = useState(false);
    return (
        <DemoCard title="Play trigger" hint="press play" testid="demo-video-button">
            <button onClick={() => { setPlaying(!playing); say(playing ? "Paused. Video hero, shop tour, product reel — we'll wire it." : "Video that starts where you want it. Show your shop, your press, your crew.", "video"); }}
                className="flex w-full items-center gap-3 rounded-2xl border border-white/12 bg-gradient-to-r from-white/[0.08] to-transparent px-4 py-3.5 text-left" data-testid="demo-video-btn">
                <span className={`flex h-10 w-10 items-center justify-center rounded-full ${playing ? "bg-white text-black" : "bg-brand-green text-black"}`}>
                    {playing ? <span className="h-3 w-3 bg-black" /> : <Play size={16} />}
                </span>
                <span>
                    <span className="block text-sm font-bold">Shop tour · 0:47</span>
                    <span className="block font-mono text-[11px] uppercase tracking-widest text-zinc-500">{playing ? "playing…" : "tap to play"}</span>
                </span>
            </button>
        </DemoCard>
    );
}

export function ArrowLinkDemo() {
    const { say } = useGuide();
    return (
        <DemoCard title="Text link with intent" hint="hover" testid="demo-arrow-link">
            <button onMouseEnter={() => say("Even the links move. Momentum makes people keep scrolling.", "arrow")}
                className="group inline-flex items-center gap-2 border-b border-brand-green/40 pb-1 text-sm font-bold text-brand-green" data-testid="demo-arrow-btn">
                See the whole menu
                <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1.5" />
            </button>
        </DemoCard>
    );
}

/* ---------------- micro-interaction extras ---------------- */

export function CounterDemo() {
    const { say } = useGuide();
    const [n, setN] = useState(0);
    const ref = useRef(null);
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const io = new IntersectionObserver(([e]) => {
            if (!e.isIntersecting) return;
            let v = 0;
            const t = setInterval(() => { v += 137; if (v >= 12480) { v = 12480; clearInterval(t); } setN(v); }, 24);
            io.disconnect();
        }, { threshold: 0.4 });
        io.observe(el);
        return () => io.disconnect();
    }, []);
    return (
        <DemoCard title="Numbers that count up" hint="scroll trigger" testid="demo-counter">
            <div ref={ref} onMouseEnter={() => say("Counters that fire when they scroll into view. Proof, right on the page.", "counter")}>
                <p className="font-display text-4xl font-black text-brand-green">{n.toLocaleString()}</p>
                <p className="mt-1 font-mono text-[11px] uppercase tracking-widest text-zinc-500">shirts printed in Brooklyn</p>
            </div>
        </DemoCard>
    );
}

export function MarqueeDemo() {
    const { say } = useGuide();
    const words = ["SAME-DAY WHITE TEES", "DTF GANG SHEETS $5/FT", "SIGNS IN 5 DAYS", "BROOKLYN SINCE 2012"];
    return (
        <DemoCard title="Endless ticker" hint="hover to slow" testid="demo-marquee">
            <div onMouseEnter={() => say("A ticker keeps your best line moving across the screen forever. Cheap attention.", "marquee")}
                className="group overflow-hidden rounded-xl border border-white/10 bg-black/40 py-3">
                <div className="flex w-max animate-[marquee_14s_linear_infinite] gap-8 group-hover:[animation-play-state:paused]">
                    {[...words, ...words].map((w, i) => (
                        <span key={i} className="font-mono text-[11px] uppercase tracking-[0.25em] text-brand-green">{w} ·</span>
                    ))}
                </div>
            </div>
        </DemoCard>
    );
}

export function TypewriterDemo() {
    const { say } = useGuide();
    const lines = ["Brooklyn's fastest printer.", "Live in 7 days.", "Built with AI, not templates."];
    const [i, setI] = useState(0);
    const [txt, setTxt] = useState("");
    useEffect(() => {
        let c = 0;
        const t = setInterval(() => {
            c += 1;
            setTxt(lines[i].slice(0, c));
            if (c >= lines[i].length) { clearInterval(t); setTimeout(() => { setI((x) => (x + 1) % lines.length); setTxt(""); }, 1500); }
        }, 55);
        return () => clearInterval(t);
    }, [i]);
    return (
        <DemoCard title="Headline that types itself" hint="watch" testid="demo-typewriter">
            <p onMouseEnter={() => say("Your headline can type itself, rotate through your services, or change per visitor.", "typewriter")}
                className="font-display text-xl font-black text-white">
                {txt}<span className="ml-0.5 inline-block h-5 w-2 animate-pulse bg-brand-green align-middle" />
            </p>
        </DemoCard>
    );
}

export function TiltCard() {
    const { say } = useGuide();
    const [t, setT] = useState({ rx: 0, ry: 0 });
    const move = (e) => {
        const r = e.currentTarget.getBoundingClientRect();
        setT({ ry: ((e.clientX - r.left) / r.width - 0.5) * 18, rx: -((e.clientY - r.top) / r.height - 0.5) * 18 });
    };
    return (
        <DemoCard title="3D tilt card" hint="move over it" testid="demo-tilt">
            <div onMouseMove={move} onMouseLeave={() => setT({ rx: 0, ry: 0 })} onMouseEnter={() => say("3D tilt. Your product cards can move like this — it feels expensive because it used to be.", "tilt")}
                style={{ perspective: 700 }}>
                <motion.div animate={{ rotateX: t.rx, rotateY: t.ry }} transition={{ type: "spring", stiffness: 150, damping: 14 }}
                    className="flex h-28 items-center justify-center rounded-2xl border border-brand-green/30 bg-[radial-gradient(circle_at_30%_20%,rgba(37, 99, 235,0.18),transparent_60%)] font-display text-lg font-black">
                    13THAV
                </motion.div>
            </div>
        </DemoCard>
    );
}

export function SpotlightCard() {
    const { say } = useGuide();
    const [p, setP] = useState({ x: 50, y: 50 });
    return (
        <DemoCard title="Cursor spotlight" hint="move over it" testid="demo-spotlight">
            <div onMouseMove={(e) => { const r = e.currentTarget.getBoundingClientRect(); setP({ x: ((e.clientX - r.left) / r.width) * 100, y: ((e.clientY - r.top) / r.height) * 100 }); }}
                onMouseEnter={() => say("The page follows the cursor. Little bit of theater goes a long way.", "spotlight")}
                className="relative h-28 overflow-hidden rounded-2xl border border-white/10 bg-black">
                <div className="absolute inset-0" style={{ background: `radial-gradient(220px circle at ${p.x}% ${p.y}%, rgba(37, 99, 235,0.22), transparent 70%)` }} />
                <p className="relative flex h-full items-center justify-center font-mono text-[11px] uppercase tracking-[0.3em] text-zinc-400">follow the light</p>
            </div>
        </DemoCard>
    );
}
